import { Component, EventEmitter, Input, Output, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ProvisioningService } from '../../services/provisioning.service';
import {
  CategoryStatus,
  FileDocumentType,
  FileProvisioningValidationResult,
  FileUploadMetadata,
  FileUploadResult,
  FileValidationIssue,
  ProvisionedFile,
} from '../../models/provisioning';
import { SnackbarService } from '../../services/snackbar.service';
import { Loader } from '../../loader/loader';

/** A file added to a zone but not yet sent — waiting for the user to click Upload. */
interface StagedFile {
  file: File;
  /** Zone id (PriorReport | DataFiles | References). */
  category: string;
  /** Set after a failed upload attempt (HTTP 400 returns the mixed per-file list, or a 200
   *  partial marks the failed rows): this row's outcome and its issue messages, rendered inline
   *  so the user can see exactly which staged files to fix or remove before retrying. */
  status?: 'Valid' | 'Warning' | 'Error';
  issues?: FileValidationIssue[];
}

/**
 * GWMR File Upload Panel (F7). 3 category drop zones (Prior Report / Data Files / References)
 * with browse fallback, client-side pre-checks, and a batch upload with partial success.
 * Uploads go through the authed API (proxy — no signing; AuthInterceptor attaches the Azure AD
 * bearer + APIM key).
 *
 * Dropped/browsed files are STAGED locally in their zone (cheap size/count pre-checks only);
 * the panel's Upload button then sends the whole session in one call to
 * POST /api/v2/report-files/upload — the server re-runs the deterministic content checks +
 * session limits and, if they pass, stores each file independently (blob + record + ingestion
 * message). A 400 hard-fails the session (stores nothing) with a per-file breakdown and keeps
 * the files staged so the user can fix and retry (story 1.5); a 200 is a partial success (some
 * files failed — the failures stay staged with their reason).
 *
 * NOTE: there is no server-side manifest read-back yet. The checklist, the generation gate, and
 * the "already provided" file list are all derived from THIS session's upload responses — a file
 * counts as provided once the upload endpoint confirms it stored. This state is per-session: a
 * reload starts empty (nothing is fetched back from the server). Repoint to a manifest/status
 * endpoint here once one exists on the v2 API.
 *
 * Stories: 50082 (zones), 50083 (browse), 50084 (batch upload + partial success),
 * 50154 (error handling: distinct 400 reasons + 409 lock), 50255 (pre-generation checklist:
 * per-category label/requirement/formats/multiplicity/status + missingRequired summary).
 */
@Component({
  selector: 'app-file-provisioning',
  standalone: true,
  imports: [CommonModule, MatIconModule, Loader],
  templateUrl: './file-provisioning.html',
  styleUrl: './file-provisioning.scss',
})
export class FileProvisioning {
  /** The project number this panel provisions files for (the API's `projectId`). It's entered/
   *  changed after this component is created, so switching it resets the panel's per-session
   *  state (nothing is re-fetched from a server). */
  private projectIdSig = signal<number | undefined>(undefined);
  @Input({ required: true })
  set projectId(value: number | undefined) {
    const next = value ? Number(value) : undefined; // form binds a string; '' / 0 => no project yet
    if (next === this.projectIdSig()) return;
    this.projectIdSig.set(next);
    // A different project: the prior project's staged/uploaded session state no longer applies.
    this.staged.set([]);
    this.uploaded.set({});
    this.sessionErrors.set([]);
    this.serverLocked.set(false);
    this.lastCorrelationId.set(null); // belongs to the old project's upload attempt
    this.uploadId.set(null); // a different project is a different generation run
    this.reportId.set(null);
    this.responseUploadId.set(null);
    this.uploadIdChange.emit(null);
  }
  get projectId(): number | undefined {
    return this.projectIdSig();
  }
  /** Client the run belongs to — required by the upload endpoint (drives the {client} blob
   *  folder). Parents pass the recipe/report clientId; sent as a string. Uploads are blocked
   *  with a clear message until it's present. */
  @Input() client: string | number | null = null;
  /** Optional site/location association, forwarded to the upload endpoint when set. */
  @Input() site: string | number | null = null;
  /** Optional parent override to lock the panel. Combined with the server lock (see isLocked). */
  @Input() locked = false;
  /** Set once the upload endpoint reports the run is locked (HTTP 409 — generation has started). */
  private serverLocked = signal(false);
  /** Effective lock: parent override OR the server-reported lock. Once locked, all uploads are
   *  disabled and a banner is shown (story: Locked run state). */
  get isLocked(): boolean {
    return this.locked || this.serverLocked();
  }
  /** Lets the parent gate its Generate button (F8 hook). */
  @Output() generationAllowedChange = new EventEmitter<boolean>();
  /** First successful upload response uploadId for this run, consumed by parent submit payload. */
  @Output() uploadIdChange = new EventEmitter<string | null>();

  // Phase-1 client-side limits (50082) — mirror the server's app-settings defaults
  // (FileProvisioningSettings). The upload endpoint is authoritative; these only spare
  // the user from shipping bytes the server would refuse.
  private static readonly MAX_FILE_BYTES = 150 * 1024 * 1024; // 150 MB/file
  private static readonly MAX_RUN_BYTES = 500 * 1024 * 1024; // 500 MB/run
  private static readonly MAX_FILES_PER_DROP = 10;

  // The upload API buckets each file by documentType (PriorReport | DataFile | References).
  // Only the Data Files zone renames — its id is plural, the documentType singular. Sent
  // per-file in the metadata array (matched by fileName).
  private static readonly ZONE_TO_DOCUMENT_TYPE: Record<string, FileDocumentType> = {
    PriorReport: 'PriorReport',
    DataFiles: 'DataFile',
    References: 'References',
  };

  private provisioning = inject(ProvisioningService);
  private snackbar = inject(SnackbarService);

  // The 3 GWMR zones are known up front, so they render as soon as "Upload" is chosen — even
  // before a project id exists. Uploaded files (this session) overlay status + the provided list.
  private static readonly DEFAULT_CATEGORIES: CategoryStatus[] = [
    { category: 'PriorReport', label: 'Prior Report', requirement: 'required', acceptedFormats: ['docx', 'dotx'], multiple: false, status: 'NotProvided', referenceKinds: null, files: [] },
    { category: 'DataFiles', label: 'Data Files', requirement: 'required', acceptedFormats: ['xlsx', 'xls', 'csv'], multiple: true, status: 'NotProvided', referenceKinds: null, files: [] },
    { category: 'References', label: 'References', requirement: 'optional', acceptedFormats: ['pdf', 'png', 'jpg', 'jpeg', 'tiff'], multiple: true, status: 'OptionalNotProvided', referenceKinds: ['Figure', 'Table', 'Appendix'], files: [] },
  ];

  /** Files this session's upload confirmed stored, keyed by zone id. Drives the "provided" status
   *  and the read-only uploaded-file rows (there is no server manifest to read them back from). */
  uploaded = signal<Record<string, ProvisionedFile[]>>({});
  private uploadedSeq = 0; // synthesises a stable track id per uploaded row (no server fileId yet)

  /** Zones to render: the 3 defaults, with each zone's status + provided files derived from this
   *  session's successful uploads. */
  categories = computed<CategoryStatus[]>(() => {
    const uploaded = this.uploaded();
    return FileProvisioning.DEFAULT_CATEGORIES.map((c) => {
      const files = uploaded[c.category] ?? [];
      const status = files.length
        ? 'Satisfied'
        : c.requirement === 'required'
          ? 'NotProvided'
          : 'OptionalNotProvided';
      return { ...c, status, files };
    });
  });

  // ----- generation gate: derived from the SAME categories() the zones render, so the badge can
  //       never say "Ready" while the required zones on screen are unsatisfied (and it re-derives
  //       automatically as uploads land).
  /** Required categories that don't yet have an uploaded file. */
  requiredMissing = computed<CategoryStatus[]>(() =>
    this.categories().filter((c) => c.requirement === 'required' && c.status !== 'Satisfied')
  );
  /** Ready once every required category has at least one successfully-uploaded file. */
  generationReady = computed<boolean>(() => this.requiredMissing().length === 0);

  // The badge distinguishes "you haven't added this" from "you added it but haven't clicked
  // Upload" — both block generation, but only the first is the user's file to go and find.
  // The gate itself stays keyed on uploads alone (staged files are not stored yet).
  /** Required zones with nothing uploaded and nothing staged either — genuinely absent. */
  requiredAbsent = computed<CategoryStatus[]>(() =>
    this.requiredMissing().filter((c) => !this.stagedFor(c.category).some((s) => s.status !== 'Error'))
  );
  /** Required zones whose file is staged and only needs the Upload click. */
  requiredAwaitingUpload = computed<CategoryStatus[]>(() =>
    this.requiredMissing().filter((c) => this.stagedFor(c.category).some((s) => s.status !== 'Error'))
  );
  /** Friendly names of the genuinely-absent required categories, for the blocked badge. */
  missingLabels = computed<string>(() => this.requiredAbsent().map((c) => c.label).join(', '));
  /** Friendly names of the staged-but-not-yet-uploaded required categories. */
  awaitingUploadLabels = computed<string>(() =>
    this.requiredAwaitingUpload().map((c) => c.label).join(', ')
  );

  // Human-readable labels for the raw status enums (NotProvided / OptionalNotProvided / … never reach the UI).
  private static readonly STATUS_LABELS: Record<string, string> = {
    Valid: 'Valid',
    NotProvided: 'Not provided',
    OptionalNotProvided: 'Optional',
    Uploaded: 'Uploaded',
    Satisfied: 'Provided',
    Warning: 'Needs attention',
    Error: 'Error',
    Pending: 'Checking…',
    Accepted: 'Accepted',
  };
  statusLabel(status: string): string {
    return FileProvisioning.STATUS_LABELS[status] ?? status;
  }

  constructor() {
    // Keep the parent's Generate gate (F8) in sync with the live, derived state.
    effect(() => this.generationAllowedChange.emit(this.generationReady()));
  }
  /** category id -> in-flight upload count, for per-zone spinners. */
  uploading = signal<Record<string, number>>({});
  dragOver = signal<string | null>(null);

  /** Files added to zones but not yet sent — the Upload button submits them as one session. */
  staged = signal<StagedFile[]>([]);
  /** True while the upload call is in flight; disables the Upload button. */
  validating = signal(false);
  /** Session-level hard failures from the last upload pass (TOO_MANY_FILES /
   *  AGGREGATE_SIZE_EXCEEDED) — not tied to any one file, shown as a banner. */
  sessionErrors = signal<FileValidationIssue[]>([]);
  /** X-Correlation-ID of the most recent upload attempt — shown on failure so the user can quote
   *  it to support, who can then pin that exact batch in the server logs. */
  lastCorrelationId = signal<string | null>(null);
  /** Groups every upload request in this generation run. Minted on the first Upload click and
   *  reused for the rest of the run, so the server can tie separate batches to one generation.
   *  (correlationId is the opposite: fresh per request.) */
  private uploadId = signal<string | null>(null);
  /** Returned by the first successful upload; echoed back on later batches in the same run. */
  private reportId = signal<string | null>(null);
  /** uploadId returned by the server in upload responses (captured from first success). */
  private responseUploadId = signal<string | null>(null);

  // ----- drag & drop / browse (50082, 50083) -----

  onDragOver(event: DragEvent, categoryId: string): void {
    if (this.isLocked) return;
    event.preventDefault();
    this.dragOver.set(categoryId);
  }

  onDragLeave(categoryId: string): void {
    if (this.dragOver() === categoryId) {
      this.dragOver.set(null);
    }
  }

  onDrop(event: DragEvent, category: CategoryStatus): void {
    event.preventDefault();
    this.dragOver.set(null);
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.stage(Array.from(files), category);
    }
  }

  onBrowse(event: Event, category: CategoryStatus): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.stage(Array.from(input.files), category);
    }
    input.value = ''; // allow re-selecting the same file
  }

  // ----- staging: client-side pre-checks (50082), nothing is sent until Upload is clicked -----

  private stage(files: File[], category: CategoryStatus): void {
    if (this.isLocked) {
      this.notifyError('Generation has started; files are locked.');
      return;
    }
    // An upload is in flight: its result handler rebuilds `staged` from the pre-upload snapshot,
    // so anything added now would be silently dropped when it resolves. Block until it finishes
    // (the Upload button is disabled for the same reason).
    if (this.validating()) {
      this.notifyError('An upload is in progress — wait for it to finish before adding more files.');
      return;
    }

    // 50082: enforce >10 files/session, >150 MB/file, >500 MB aggregate/run — before staging.
    // The upload endpoint re-checks all of these server-side (configurable limits); these
    // mirrors only spare the user from shipping bytes the server would refuse anyway. The count
    // cap is enforced per-file below (not as a whole-drop pre-check) so files that get skipped
    // here — duplicates, empty, oversize — don't consume a slot and wrongly reject the drop.
    let runBytes = this.currentRunBytes() + this.stagedBytes();
    // The upload metadata (documentType) and the per-file result matching both key on fileName
    // (case-insensitive, first unclaimed hit), so two files sharing a name would pair off
    // ambiguously. Reject a name that collides with an already-staged, already-uploaded
    // (this session), or earlier-in-this-drop file.
    const takenNames = new Set<string>([
      ...this.staged().map((s) => s.file.name.toLowerCase()),
      ...Object.values(this.uploaded()).flat().map((f) => f.fileName.toLowerCase()),
    ]);
    // Single-file zones (Prior Report) cap at one file across staged + already-uploaded. The
    // browse input's [multiple]="false" enforces this for picking, but drag & drop bypasses it.
    let zoneCount = !category.multiple
      ? this.stagedFor(category.category).length + (this.uploaded()[category.category]?.length ?? 0)
      : 0;
    const additions: StagedFile[] = [];
    for (const f of files) {
      // 50154: client-side validation before sending — clear, per-file, dismissible, non-blocking.
      if (!category.multiple && zoneCount >= 1) {
        this.notifyError(`${category.label} accepts a single file — remove the current one first.`);
        break; // the zone is full; nothing later in this drop can fit either
      }
      const nameKey = f.name.toLowerCase();
      if (takenNames.has(nameKey)) {
        this.notifyError(`A file named "${f.name}" is already in this session; rename it or remove the other one first.`);
        continue;
      }
      if (f.size === 0) {
        this.notifyError(`"${f.name}" is empty (0 bytes) and was not added.`);
        continue;
      }
      // 50154: extension vs the zone's accepted formats. Drag & drop bypasses the browse input's
      // `accept`, so this is the only guard on that path. A file that belongs to a DIFFERENT zone
      // is still staged — marked Error, naming the zone it belongs in — so the user can see and
      // correct the mistake instead of watching a file vanish behind a snackbar. Staged errors
      // block Upload (see stagedErrors). A format no zone accepts can never become valid, so it
      // is refused outright. Keep these lists in step with the server's per-documentType config.
      const dot = f.name.lastIndexOf('.');
      const ext = dot > 0 ? f.name.slice(dot + 1).toLowerCase() : '';
      let misplacedIn: CategoryStatus | undefined;
      if (!category.acceptedFormats.includes(ext)) {
        misplacedIn = FileProvisioning.DEFAULT_CATEGORIES.find((c) => c.acceptedFormats.includes(ext));
        if (!misplacedIn) {
          this.notifyError(
            `"${f.name}" isn't a supported file type and was not added — accepted types: ${FileProvisioning.allAcceptedFormats().join(', ')}.`
          );
          continue;
        }
      }
      // Content-level checks (corruption, password protection) stay the upload endpoint's job —
      // they need byte inspection the client shouldn't attempt.
      if (f.size > FileProvisioning.MAX_FILE_BYTES) {
        this.notifyError(`"${f.name}" exceeds 150 MB and was not added.`);
        continue;
      }
      if (runBytes + f.size > FileProvisioning.MAX_RUN_BYTES) {
        this.notifyError(`"${f.name}" would exceed the 500 MB per-report limit and was not added.`);
        continue;
      }
      // Count only files that passed the checks above; once the session is at the cap, no further
      // file can be added, so stop (any remainder would be rejected for the same reason).
      if (this.staged().length + additions.length >= FileProvisioning.MAX_FILES_PER_DROP) {
        this.notifyError(`Up to ${FileProvisioning.MAX_FILES_PER_DROP} files per upload session.`);
        break;
      }
      runBytes += f.size;
      zoneCount++; // a single-file zone is now full for the rest of this drop
      takenNames.add(nameKey); // claim it so a same-named sibling later in this drop is rejected too
      additions.push({
        file: f,
        category: category.category,
        ...(misplacedIn
          ? {
              status: 'Error' as const,
              issues: [
                {
                  code: 'WRONG_ZONE',
                  message: `${ext.toUpperCase()} files belong in ${misplacedIn.label} — move it there, or remove it, before uploading.`,
                },
              ],
            }
          : {}),
      });
    }
    if (additions.length) {
      this.staged.set([...this.staged(), ...additions]);
      this.sessionErrors.set([]); // session composition changed; the last verdict is stale
    }
  }

  /** Remove a staged (not yet uploaded) file. */
  removeStaged(item: StagedFile): void {
    this.staged.set(this.staged().filter((s) => s !== item));
    this.sessionErrors.set([]); // session composition changed; the last verdict is stale
  }

  /** Staged files shown inside a zone. */
  stagedFor(categoryId: string): StagedFile[] {
    return this.staged().filter((s) => s.category === categoryId);
  }

  /** Every extension any zone accepts — for the "not a supported file type" message. */
  private static allAcceptedFormats(): string[] {
    return [...new Set(FileProvisioning.DEFAULT_CATEGORIES.flatMap((c) => c.acceptedFormats))];
  }

  /** Staged rows the user must fix (wrong zone, or a failed prior attempt) before Upload is
   *  allowed. Uploading a known-bad batch would only round-trip to the same rejection. */
  stagedErrors = computed<StagedFile[]>(() => this.staged().filter((s) => s.status === 'Error'));

  /** Move a misplaced staged file into the zone that accepts it, clearing its error mark. */
  moveStaged(item: StagedFile, categoryId: string): void {
    const target = FileProvisioning.DEFAULT_CATEGORIES.find((c) => c.category === categoryId);
    if (!target) return;
    // The destination may be a full single-file zone — moving into it would breach the same cap
    // stage() enforces, so refuse with the same message rather than silently over-filling it.
    if (
      !target.multiple &&
      this.stagedFor(categoryId).length + (this.uploaded()[categoryId]?.length ?? 0) >= 1
    ) {
      this.notifyError(`${target.label} accepts a single file — remove the current one first.`);
      return;
    }
    this.staged.set(
      this.staged().map((s) =>
        s === item ? { file: s.file, category: categoryId } : s
      )
    );
    this.sessionErrors.set([]); // session composition changed; the last verdict is stale
  }

  /** The zone a misplaced staged file should move to, or null when it isn't misplaced. */
  targetZoneFor(item: StagedFile): CategoryStatus | null {
    const dot = item.file.name.lastIndexOf('.');
    const ext = dot > 0 ? item.file.name.slice(dot + 1).toLowerCase() : '';
    if (item.status !== 'Error') return null;
    const zone = FileProvisioning.DEFAULT_CATEGORIES.find((c) => c.acceptedFormats.includes(ext));
    return zone && zone.category !== item.category ? zone : null;
  }

  private stagedBytes(): number {
    return this.staged().reduce((sum, s) => sum + s.file.size, 0);
  }

  /**
   * The Upload button: submit the whole staged session to the backend Upload API in one call
   * (POST /api/v2/report-files/upload). The server re-runs the hard validate checks and,
   * if they pass, blobs + persists + queues each file independently.
   * - HTTP 400: nothing is stored; the per-file validation breakdown from the envelope's
   *   `errors` is rendered inline and the files STAY staged so the user can fix/remove and retry.
   * - HTTP 201: every file was stored — the staged rows move to the uploaded list.
   * - HTTP 200: partial — some files failed. The succeeded ones move to the uploaded list; the
   *   failed ones stay staged, marked with the server's reason.
   */
  uploadStaged(): void {
    if (this.isLocked) {
      this.notifyError('Generation has started; files are locked.');
      return;
    }
    const projectId = this.projectIdSig();
    if (!projectId) {
      this.notifyError('Enter a project number before uploading files.');
      return;
    }
    const client = this.client != null ? String(this.client).trim() : '';
    if (!client) {
      this.notifyError('A client is required before uploading files.');
      return;
    }
    let items = this.staged();
    if (!items.length || this.validating()) {
      return;
    }
    // Files marked Error (wrong zone, or a failed prior attempt) must be fixed first — sending
    // them would just round-trip to the same rejection, and on a 400 nothing at all gets stored.
    const blocked = this.stagedErrors();
    if (blocked.length) {
      this.notifyError(
        `Fix or remove ${blocked.length} file(s) before uploading: ${blocked.map((s) => `"${s.file.name}"`).join(', ')}.`
      );
      return;
    }

    // This attempt gets a fresh verdict — drop the previous pass's row marks and banner.
    this.sessionErrors.set([]);
    items = items.map(({ status, issues, ...rest }) => rest);
    this.staged.set(items);

    const metadata: FileUploadMetadata[] = items.map((it) => ({
      fileName: it.file.name,
      documentType: FileProvisioning.ZONE_TO_DOCUMENT_TYPE[it.category],
    }));

    // One id per Upload click, so a batch can be traced end-to-end in the server logs. The
    // interceptor's per-tab session id would lump every attempt together, which is useless once
    // the user has retried; this is quoted back to them on failure for support.
    const correlationId = crypto.randomUUID();
    this.lastCorrelationId.set(correlationId);

    // One id for the whole run: minted on the first Upload click, reused by every later batch.
    let uploadId = this.uploadId();
    if (!uploadId) {
      uploadId = crypto.randomUUID();
      this.uploadId.set(uploadId);
    }

    this.validating.set(true);
    for (const it of items) this.bumpUploading(it.category, 1); // per-zone spinners for the batch
    this.provisioning
      .uploadFiles(
        {
          projectId, // the panel's project-number input, sent as projectId in the payload
          client,
          site: this.site != null ? String(this.site) : null,
          metadata,
          uploadId,
          reportId: this.reportId(),
          correlationId,
        },
        items.map((it) => it.file)
      )
      .subscribe({
        next: (resp) => {
          this.validating.set(false);
          for (const it of items) this.bumpUploading(it.category, -1);
          this.handleUploadResult(resp.body?.data, items);
        },
        error: (err: any) => {
          this.validating.set(false);
          for (const it of items) this.bumpUploading(it.category, -1);
          this.reportUploadFailure(err); // files stay staged for fixing and retrying
        },
      });
  }

  /**
   * HTTP 200/201 — the batch was processed. Succeeded files move from staging into the uploaded
   * list (which drives the zone status + generation gate); failed files stay staged, marked with
   * the server's reason so the user can fix or remove them and retry. A 200 carries at least one
   * failure. Items are matched to staged rows by fileName (first unclaimed hit, so duplicate names
   * pair off one-to-one).
   */
  private handleUploadResult(result: FileUploadResult | undefined, items: StagedFile[]): void {
    // The server assigns the report on the first batch; later batches in this run echo it back.
    if (result?.reportId) {
      this.reportId.set(String(result.reportId));
    }
    // Capture the uploadId from the first successful upload response for parent submit payloads.
    if (!this.responseUploadId() && result?.uploadId) {
      const serverUploadId = String(result.uploadId);
      this.responseUploadId.set(serverUploadId);
      this.uploadIdChange.emit(serverUploadId);
    }
    const resultFiles = result?.items ?? [];
    const claimed = new Set<number>();
    const stillStaged: StagedFile[] = [];
    const failureLines: string[] = [];
    const newlyUploaded: Record<string, ProvisionedFile[]> = {};
    for (const s of items) {
      const idx = resultFiles.findIndex(
        (r, i) => !claimed.has(i) && r.fileName?.toLowerCase() === s.file.name.toLowerCase()
      );
      let r: FileUploadResult['items'][number] | undefined;
      if (idx >= 0) {
        claimed.add(idx);
        r = resultFiles[idx];
      }
      // status is 'Succeeded' as a string or 0 as the numeric enum, depending on server config.
      if (r && (r.status === 'Succeeded' || r.status === 0)) {
        (newlyUploaded[s.category] ??= []).push({
          fileId: ++this.uploadedSeq,
          fileName: s.file.name,
          sizeBytes: s.file.size,
          validationStatus: 'Accepted',
        });
        continue; // stored — drop from staging
      }
      // failed (or, defensively, missing from the response): keep it staged with the reason
      const reason = r?.error || 'Upload failed.';
      stillStaged.push({ ...s, status: 'Error', issues: [{ code: 'UPLOAD_FAILED', message: reason }] });
      failureLines.push(`"${s.file.name}": ${reason}`);
    }
    this.staged.set(stillStaged);

    // Merge the succeeded files into the confirmed list (accumulates across Upload clicks).
    const uploadedCats = Object.keys(newlyUploaded);
    if (uploadedCats.length) {
      const merged = { ...this.uploaded() };
      for (const cat of uploadedCats) {
        merged[cat] = [...(merged[cat] ?? []), ...newlyUploaded[cat]];
      }
      this.uploaded.set(merged);
    }

    const succeeded = items.length - failureLines.length;
    if (failureLines.length) {
      this.notifyError(
        this.withRef(
          `${failureLines.length} file(s) failed to upload. ${failureLines.join(' ')} ` +
            'Fix or remove them and click Upload again.'
        )
      );
    } else if (succeeded > 0) {
      this.notifySuccess(`${succeeded} file${succeeded === 1 ? '' : 's'} uploaded successfully.`);
    }
  }

  // ----- view helpers -----

  uploadingCount(categoryId: string): number {
    return this.uploading()[categoryId] ?? 0;
  }

  accept(category: CategoryStatus): string {
    return category.acceptedFormats.map((f) => '.' + f).join(',');
  }

  /** Human-readable size — scales the unit so small files don't render as "0 MB". */
  formatSize(bytes: number | undefined | null): string {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** Upload hard-failed (HTTP 400): nothing gets stored — not even the valid files.
   *  On a validation hard-fail the envelope's `errors` carries a FileProvisioningValidationResult
   *  (isValid / sessionErrors / files, with valid and warning files still reported), so the
   *  staged rows render the mixed per-file list inline: each row gets its status + issue
   *  messages, and the session-level failures (TOO_MANY_FILES / AGGREGATE_SIZE_EXCEEDED)
   *  show in the banner. Scenarios with `errors: null` (no files sent, malformed metadata/request,
   *  server error) and network failures fall back to the envelope's message. */
  private reportUploadFailure(err: any): void {
    // 50156: generation started mid-session and the run is now locked (409) — say so distinctly
    // instead of a generic "Upload failed", and flip the panel into its read-only locked state.
    if (err?.status === 409) {
      this.notifyError('Cannot upload: generation has started; files are locked.');
      this.serverLocked.set(true);
      return;
    }
    const result: FileProvisioningValidationResult | undefined =
      err?.status === 400 ? err?.error?.errors ?? undefined : undefined;
    if (!result) {
      this.notifyError(this.withRef(err?.error?.message || 'Upload failed; nothing was stored.'));
      return;
    }

    this.sessionErrors.set(result.sessionErrors ?? []);

    // Annotate each staged row with its outcome, matched by file name (first unclaimed hit,
    // so duplicate names pair off one-to-one).
    const files = result.files ?? [];
    const claimed = new Set<number>();
    this.staged.set(
      this.staged().map((s) => {
        const idx = files.findIndex(
          (f, i) => !claimed.has(i) && f.fileName?.toLowerCase() === s.file.name.toLowerCase()
        );
        if (idx < 0) return s;
        claimed.add(idx);
        const f = files[idx];
        return { ...s, status: f.validationStatus, issues: [...(f.errors ?? []), ...(f.warnings ?? [])] };
      })
    );

    this.notifyError(
      this.withRef('Nothing was stored — fix or remove the files that failed validation, then click Upload again.')
    );
  }

  /** Bytes already uploaded this session — counts toward the per-run size limit. */
  private currentRunBytes(): number {
    return Object.values(this.uploaded()).reduce(
      (sum, files) => sum + files.reduce((s, f) => s + (f.sizeBytes ?? 0), 0),
      0
    );
  }

  private bumpUploading(categoryId: string, delta: number): void {
    const next = { ...this.uploading() };
    next[categoryId] = Math.max(0, (next[categoryId] ?? 0) + delta);
    this.uploading.set(next);
  }

  /** Tag an upload-failure message with the attempt's correlation id so the user can quote it. */
  private withRef(message: string): string {
    const id = this.lastCorrelationId();
    return id ? `${message} (Ref: ${id})` : message;
  }

  private notifyError(message: string): void {
    this.snackbar.showCustom({ type: 'error', title: 'File provisioning', message });
  }

  private notifySuccess(message: string): void {
    this.snackbar.showCustom({ type: 'success', title: 'File provisioning', message });
  }
}
